import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, Table, Tag, Typography, Button, Input, Space, message, Alert } from 'antd';
import { ReloadOutlined, LinkOutlined, RobotOutlined } from '@ant-design/icons';
import { aiSalesAPI } from '../services/api';
import AlayaessAgentPicker, { AlayaessAgentPickerHint } from '../components/AlayaessAgentPicker';

const { Title, Text } = Typography;

const KIND_LABELS = {
  ai_sales_bot: { label: 'AI Sales Bot', color: 'blue' },
  chatbot_config: { label: 'Chatbot', color: 'purple' },
  scenario_site: { label: 'Scenario Site', color: 'green' },
};

export default function AlayaessBindingsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [searchParams] = useSearchParams();
  const highlightKey = searchParams.get('highlight');  // "kind:id"

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiSalesAPI.listAlayaessBindings();
      const body = res.data ?? res;
      setRows(body.rows || []);
    } catch (e) {
      message.error(`載入失敗：${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleBind = async (row, newName) => {
    const key = `${row.kind}:${row.id}`;
    setSavingKey(key);
    try {
      await aiSalesAPI.updateAlayaessBinding(row.kind, row.id, { alayaess_bot_name: newName });
      setRows(prev => prev.map(r =>
        r.kind === row.kind && r.id === row.id ? { ...r, alayaess_bot_name: newName || null } : r
      ));
      message.success(newName ? `已綁定到 ${newName}` : '已解除綁定');
    } catch (e) {
      message.error(`儲存失敗：${e.message}`);
    } finally {
      setSavingKey(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.name?.toLowerCase().includes(q)
      || r.subtitle?.toLowerCase().includes(q)
      || (r.alayaess_bot_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const summary = useMemo(() => {
    const total = rows.length;
    const bound = rows.filter(r => r.alayaess_bot_name).length;
    return { total, bound, pct: total ? Math.round((bound / total) * 100) : 0 };
  }, [rows]);

  const columns = [
    {
      title: '類型', dataIndex: 'kind', width: 130,
      filters: Object.entries(KIND_LABELS).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (v, r) => r.kind === v,
      render: k => {
        const m = KIND_LABELS[k] || { label: k, color: 'default' };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: 'SPO Bot 名稱', dataIndex: 'name', width: 280,
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.subtitle}</Text>
        </div>
      ),
    },
    {
      title: 'AlayaEss Agent 綁定', width: 360,
      render: (_, row) => {
        const key = `${row.kind}:${row.id}`;
        return (
          <AlayaessAgentPicker
            value={row.alayaess_bot_name}
            onChange={(v) => handleBind(row, v)}
            size="small"
            style={{ width: '100%' }}
            disabled={savingKey === key}
          />
        );
      },
    },
    {
      title: '狀態', width: 90, align: 'center',
      render: (_, r) => r.alayaess_bot_name
        ? <Tag color="success">已綁定</Tag>
        : <Tag>未綁定</Tag>,
    },
    {
      title: '前往編輯', width: 120, align: 'center',
      render: (_, r) => (
        <Link to={r.edit_url}><Button size="small" icon={<LinkOutlined />}>原始頁</Button></Link>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <RobotOutlined /> AlayaEss Bot 綁定管理
          </Title>
          <Text type="secondary">把 SPO 上每一個 bot（AI Sales / Chatbot / Scenario Site）綁到 AlayaEss 上對應的真實 agent，統一在這裡管。</Text>
        </div>
        <Space>
          <Input.Search
            placeholder="搜尋 bot / agent name"
            allowClear
            style={{ width: 240 }}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchRows} loading={loading}>重新整理</Button>
        </Space>
      </div>

      <div style={{ marginBottom: 12 }}>
        <AlayaessAgentPickerHint />
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <span>
            目前共 <b>{summary.total}</b> 個 SPO bot，<b>{summary.bound}</b> 個已綁定（{summary.pct}%）。
            未綁定的 bot 會 fallback 到 Mode A（共用 Mika 的人設）。
          </span>
        }
      />

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey={(r) => `${r.kind}:${r.id}`}
          loading={loading}
          pagination={{ pageSize: 30 }}
          size="middle"
          rowClassName={(r) => `${r.kind}:${r.id}` === highlightKey ? 'highlighted-binding-row' : ''}
        />
      </Card>

      <style>{`
        .highlighted-binding-row > td {
          background: #fef9c3 !important;
          animation: highlight-fade 4s ease-out;
        }
        @keyframes highlight-fade {
          0%   { background: #fde047; }
          100% { background: #fef9c3; }
        }
      `}</style>
    </div>
  );
}
